/**
 * Blockchain service for interacting with BWS Token Market Maker smart contracts
 * Handles both read and write operations with the deployed contracts
 */

import { ethers } from 'ethers';
import { config, debugLog } from '../utils/config';
import { infuraRateLimiter, RateLimiter } from '../utils/rateLimiter';

// Contract ABIs (minimal interfaces for the functions we need)
const TREASURY_ABI = [
  'function getTreasuryStats() view returns (uint256 totalDeposited, uint256 totalAuthorized, uint256 availableForAuthorization)',
  'function totalRevenueDeposited() view returns (uint256)',
  'function totalAuthorizedForPurchase() view returns (uint256)',
  'function maxPurchasePercentage() view returns (uint256)',
  'function owner() view returns (address)',
  'function paused() view returns (bool)',
  'event RevenueDeposited(address indexed depositor, uint256 amount)',
  'event PurchaseAuthorized(address indexed marketMaker, uint256 amount)'
];

const VAULT_ABI = [
  'function getVaultStats() view returns (uint256 totalEthReceived, uint256 totalTokensHeld)',
  'function getTokenUtilization() view returns (uint256 totalHeld, uint256 totalWithdrawn, uint256 availableForWithdrawal)',
  'function paused() view returns (bool)',
  'event TokensWithdrawnForTrading(address indexed trader, uint256 amount)'
];

const AUDIT_TRAIL_ABI = [
  'function getAuditStats() view returns (uint256 totalPurchases, uint256 totalEthSpent, uint256 totalTokensPurchased)',
  'function getTotalPurchases() view returns (uint256)',
  'function getPurchaseRecord(uint256 index) view returns (address vault, uint256 ethAmount, uint256 tokenAmount, string exchange, uint256 timestamp)',
  'function getExchangeDistribution() view returns (string[] exchanges, uint256[] amounts)',
  'event TokenPurchaseRecorded(address indexed vault, uint256 ethAmount, uint256 tokenAmount, string exchange)'
];

const BWS_TOKEN_ABI = [
  'function totalSupply() view returns (uint256)',
  'function balanceOf(address account) view returns (uint256)',
  'function symbol() view returns (string)',
  'function name() view returns (string)',
  'function decimals() view returns (uint8)'
];

export interface TreasuryStats {
  totalDeposited: string;
  totalAuthorized: string;
  availableForAuthorization: string;
  maxPurchasePercentage: number;
  owner: string;
  paused: boolean;
}

export interface VaultStats {
  totalEthReceived: string;
  totalTokensHeld: string;
  tokenUtilization: {
    totalHeld: string;
    totalWithdrawn: string;
    availableForWithdrawal: string;
  };
  paused: boolean;
}

export interface AuditStats {
  totalPurchases: number;
  totalEthSpent: string;
  totalTokensPurchased: string;
}

export interface PurchaseRecord {
  index: number;
  vault: string;
  ethAmount: string;
  tokenAmount: string;
  exchange: string;
  timestamp: number;
}

export interface ExchangeDistribution {
  exchange: string;
  amount: string;
  percentage: number;
}

export interface TokenInfo {
  name: string;
  symbol: string;
  decimals: number;
  totalSupply: string;
  vaultBalance: string;
}

export interface SystemStatus {
  connected: boolean;
  chainId: number;
  blockNumber: number;
  networkName: string;
  contractsDeployed: {
    treasury: boolean;
    vault: boolean;
    auditTrail: boolean;
    bwsToken: boolean;
  };
}

interface CachedData<T> {
  data: T;
  timestamp: number;
  ttl: number;
}

class BlockchainService {
  private provider: ethers.JsonRpcProvider | null = null;
  private treasuryContract: ethers.Contract | null = null;
  private vaultContract: ethers.Contract | null = null;
  private auditTrailContract: ethers.Contract | null = null;
  private bwsTokenContract: ethers.Contract | null = null;
  private connected = false;
  private rateLimiter: RateLimiter;

  // Static data cache
  private staticDataCache: Map<string, CachedData<any>> = new Map();
  private STATIC_DATA_TTL = 24 * 60 * 60 * 1000; // 24 hours for static data
  private DYNAMIC_DATA_TTL = 60 * 1000; // 1 minute for dynamic data

  constructor() {
    // Detect if we're using Infura and configure rate limiting appropriately
    const isUsingInfura = config.blockchain.rpcUrl.includes('infura.io');

    if (isUsingInfura) {
      // Use the predefined Infura rate limiter
      this.rateLimiter = infuraRateLimiter;
      debugLog('Using Infura rate limiter for API calls');
    } else {
      // For local or other providers, use more lenient rate limiting
      this.rateLimiter = new RateLimiter({
        maxRequestsPerSecond: 50,
        burstLimit: 20,
        retryAttempts: 2,
        retryDelayBase: 500,
      });
      debugLog('Using lenient rate limiter for local/other RPC');
    }

    this.initialize();
  }

  /**
   * Rate-limited wrapper for provider calls
   */
  private async callProvider<T>(method: string, ...args: any[]): Promise<T> {
    if (!this.provider) {
      throw new Error('Provider not initialized');
    }

    return this.rateLimiter.executeWithLimit(async () => {
      debugLog(`Making rate-limited provider call: ${method}`, args.length > 0 ? args : undefined);
      const result = await (this.provider as any)[method](...args);
      return result;
    });
  }

  /**
   * Rate-limited wrapper for contract calls
   */
  private async callContract<T>(contract: ethers.Contract, method: string, ...args: any[]): Promise<T> {
    return this.rateLimiter.executeWithLimit(async () => {
      debugLog(`Making rate-limited contract call: ${method}`, args.length > 0 ? args : undefined);
      const result = await contract[method](...args);
      return result;
    });
  }

  /**
   * Get cached data if still valid
   */
  private getCachedData<T>(key: string): T | null {
    const cached = this.staticDataCache.get(key);
    if (cached && Date.now() - cached.timestamp < cached.ttl) {
      debugLog(`Cache hit for ${key}`);
      return cached.data as T;
    }
    return null;
  }

  /**
   * Set cached data
   */
  private setCachedData<T>(key: string, data: T, ttl: number): void {
    this.staticDataCache.set(key, {
      data,
      timestamp: Date.now(),
      ttl
    });
    debugLog(`Cached ${key} with TTL ${ttl}ms`);
  }

  private async initialize(): Promise<void> {
    try {
      // Validate configuration
      if (!config.blockchain.rpcUrl) {
        throw new Error('No RPC URL configured. Please set REACT_APP_ETH_RPC_URL environment variable.');
      }

      // Initialize provider
      debugLog('Connecting to blockchain via:', config.blockchain.rpcUrl);
      this.provider = new ethers.JsonRpcProvider(config.blockchain.rpcUrl);

      // Test connection with timeout using rate limiter
      const networkPromise = this.callProvider('getNetwork');
      const timeoutPromise = new Promise((_, reject) =>
        setTimeout(() => reject(new Error('Connection timeout')), 15000) // Increased timeout due to rate limiting
      );

      await Promise.race([networkPromise, timeoutPromise]);

      // Initialize contracts if addresses are provided
      await this.initializeContracts();

      this.connected = true;
      debugLog('Blockchain service initialized successfully');
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      console.error('Failed to initialize blockchain service:', errorMessage);
      debugLog('Blockchain initialization error:', error);
      this.connected = false;

      // Provide helpful error messages
      if (errorMessage.includes('timeout')) {
        console.error('The blockchain RPC endpoint is not responding. Check your network connection and RPC URL.');
      } else if (errorMessage.includes('No RPC URL')) {
        console.error('RPC URL is not configured. The website needs a valid Ethereum RPC endpoint.');
      } else if (errorMessage.includes('NETWORK_ERROR')) {
        console.error('Cannot connect to the blockchain network. The RPC endpoint may be down or unreachable.');
      }
    }
  }

  private async initializeContracts(): Promise<void> {
    if (!this.provider) {
      throw new Error('Provider not initialized');
    }

    const { contracts } = config.blockchain;
    let contractsFound = 0;

    // Validate and initialize Treasury contract
    if (contracts.treasury && contracts.treasury !== '' && contracts.treasury !== '0x0000000000000000000000000000000000000000') {
      try {
        this.treasuryContract = new ethers.Contract(
          contracts.treasury,
          TREASURY_ABI,
          this.provider
        );
        contractsFound++;
        debugLog('Treasury contract initialized:', contracts.treasury);
      } catch (error) {
        console.error('Failed to initialize Treasury contract:', error);
      }
    } else {
      debugLog('Treasury contract address not configured');
    }

    // Validate and initialize Vault contract
    if (contracts.vault && contracts.vault !== '' && contracts.vault !== '0x0000000000000000000000000000000000000000') {
      try {
        this.vaultContract = new ethers.Contract(
          contracts.vault,
          VAULT_ABI,
          this.provider
        );
        contractsFound++;
        debugLog('Vault contract initialized:', contracts.vault);
      } catch (error) {
        console.error('Failed to initialize Vault contract:', error);
      }
    } else {
      debugLog('Vault contract address not configured');
    }

    // Validate and initialize AuditTrail contract
    if (contracts.auditTrail && contracts.auditTrail !== '' && contracts.auditTrail !== '0x0000000000000000000000000000000000000000') {
      try {
        this.auditTrailContract = new ethers.Contract(
          contracts.auditTrail,
          AUDIT_TRAIL_ABI,
          this.provider
        );
        contractsFound++;
        debugLog('AuditTrail contract initialized:', contracts.auditTrail);
      } catch (error) {
        console.error('Failed to initialize AuditTrail contract:', error);
      }
    } else {
      debugLog('AuditTrail contract address not configured');
    }

    // Validate and initialize BWS Token contract
    if (contracts.bwsToken && contracts.bwsToken !== '' && contracts.bwsToken !== '0x0000000000000000000000000000000000000000') {
      try {
        this.bwsTokenContract = new ethers.Contract(
          contracts.bwsToken,
          BWS_TOKEN_ABI,
          this.provider
        );
        contractsFound++;
        debugLog('BWS Token contract initialized:', contracts.bwsToken);
      } catch (error) {
        console.error('Failed to initialize BWS Token contract:', error);
      }
    } else {
      debugLog('BWS Token contract address not configured');
    }

    if (contractsFound === 0) {
      console.warn('⚠️ No smart contracts configured. The website will display limited information.');
      console.warn('Please ensure contract addresses are set in the environment configuration.');
    } else {
      debugLog(`Initialized ${contractsFound} smart contracts`);
    }
  }

  /**
   * Get system status and connection info
   */
  async getSystemStatus(): Promise<SystemStatus> {
    if (!this.provider) {
      return {
        connected: false,
        chainId: 0,
        blockNumber: 0,
        networkName: 'Disconnected',
        contractsDeployed: {
          treasury: false,
          vault: false,
          auditTrail: false,
          bwsToken: false
        }
      };
    }

    try {
      const network = await this.callProvider<{ name: string; chainId: bigint }>('getNetwork');
      const blockNumber = await this.callProvider<number>('getBlockNumber');

      return {
        connected: this.connected,
        chainId: Number(network.chainId),
        blockNumber,
        networkName: network.name || 'Unknown',
        contractsDeployed: {
          treasury: !!this.treasuryContract,
          vault: !!this.vaultContract,
          auditTrail: !!this.auditTrailContract,
          bwsToken: !!this.bwsTokenContract
        }
      };
    } catch (error) {
      debugLog('Error getting system status', error);
      throw error;
    }
  }

  /**
   * Get Treasury contract statistics
   */
  async getTreasuryStats(): Promise<TreasuryStats> {
    if (!this.treasuryContract) {
      throw new Error('Treasury contract not initialized');
    }

    try {
      const [stats, maxPercentage, owner, paused] = await Promise.all([
        this.callContract<{totalDeposited: bigint; totalAuthorized: bigint; availableForAuthorization: bigint}>(this.treasuryContract, 'getTreasuryStats'),
        this.callContract<bigint>(this.treasuryContract, 'maxPurchasePercentage'),
        this.callContract<string>(this.treasuryContract, 'owner'),
        this.callContract<boolean>(this.treasuryContract, 'paused')
      ]);

      return {
        totalDeposited: ethers.formatEther(stats.totalDeposited),
        totalAuthorized: ethers.formatEther(stats.totalAuthorized),
        availableForAuthorization: ethers.formatEther(stats.availableForAuthorization),
        maxPurchasePercentage: Number(maxPercentage),
        owner,
        paused
      };
    } catch (error) {
      debugLog('Error getting treasury stats', error);
      throw error;
    }
  }

  /**
   * Get MarketMakingVault statistics
   */
  async getVaultStats(): Promise<VaultStats> {
    if (!this.vaultContract) {
      throw new Error('Vault contract not initialized');
    }

    try {
      const [stats, utilization, paused] = await Promise.all([
        this.callContract<{totalEthReceived: bigint; totalTokensHeld: bigint}>(this.vaultContract, 'getVaultStats'),
        this.callContract<{totalHeld: bigint; totalWithdrawn: bigint; availableForWithdrawal: bigint}>(this.vaultContract, 'getTokenUtilization'),
        this.callContract<boolean>(this.vaultContract, 'paused')
      ]);

      return {
        totalEthReceived: ethers.formatEther(stats.totalEthReceived),
        totalTokensHeld: ethers.formatUnits(stats.totalTokensHeld, 9), // BWS has 9 decimals
        tokenUtilization: {
          totalHeld: ethers.formatUnits(utilization.totalHeld, 9), // BWS has 9 decimals
          totalWithdrawn: ethers.formatUnits(utilization.totalWithdrawn, 9), // BWS has 9 decimals
          availableForWithdrawal: ethers.formatUnits(utilization.availableForWithdrawal, 9) // BWS has 9 decimals
        },
        paused
      };
    } catch (error) {
      debugLog('Error getting vault stats', error);
      throw error;
    }
  }

  /**
   * Get AuditTrail statistics
   */
  async getAuditStats(): Promise<AuditStats> {
    if (!this.auditTrailContract) {
      throw new Error('Audit trail contract not initialized');
    }

    try {
      const stats = await this.callContract<{totalPurchases: bigint; totalEthSpent: bigint; totalTokensPurchased: bigint}>(this.auditTrailContract, 'getAuditStats');

      return {
        totalPurchases: Number(stats.totalPurchases),
        totalEthSpent: ethers.formatEther(stats.totalEthSpent),
        totalTokensPurchased: ethers.formatUnits(stats.totalTokensPurchased, 9) // BWS has 9 decimals
      };
    } catch (error) {
      debugLog('Error getting audit stats', error);
      throw error;
    }
  }

  /**
   * Get recent purchase records
   */
  async getRecentPurchases(limit: number = 10): Promise<PurchaseRecord[]> {
    if (!this.auditTrailContract) {
      console.error('Audit trail contract not initialized');
      throw new Error('Audit trail contract not initialized');
    }

    try {
      console.log('Fetching recent purchases from AuditTrail contract:', config.blockchain.contracts.auditTrail);

      const totalPurchases = await this.callContract<bigint>(this.auditTrailContract, 'getTotalPurchases');
      const total = Number(totalPurchases);

      console.log('Total purchases recorded in AuditTrail:', total);

      if (total === 0) {
        console.log('No purchases recorded yet');
        return [];
      }

      const startIndex = Math.max(0, total - limit);
      const purchases: PurchaseRecord[] = [];

      console.log(`Fetching purchases from index ${startIndex} to ${total - 1}`);

      for (let i = startIndex; i < total; i++) {
        const record = await this.callContract<{vault: string; ethAmount: bigint; tokenAmount: bigint; exchange: string; timestamp: bigint}>(this.auditTrailContract, 'getPurchaseRecord', i);

        const purchase = {
          index: i,
          vault: record.vault,
          ethAmount: ethers.formatEther(record.ethAmount),
          tokenAmount: ethers.formatUnits(record.tokenAmount, 9), // BWS has 9 decimals
          exchange: record.exchange,
          timestamp: Number(record.timestamp)
        };

        console.log(`Purchase ${i}:`, purchase);
        purchases.push(purchase);
      }

      return purchases.reverse(); // Most recent first
    } catch (error) {
      console.error('Error getting recent purchases:', error);
      debugLog('Error getting recent purchases', error);
      throw error;
    }
  }

  /**
   * Get exchange distribution data
   */
  async getExchangeDistribution(): Promise<ExchangeDistribution[]> {
    if (!this.auditTrailContract) {
      throw new Error('Audit trail contract not initialized');
    }

    try {
      // First check if there are any purchases
      const totalPurchases = await this.callContract<bigint>(this.auditTrailContract, 'getTotalPurchases');

      // If no purchases, return empty array instead of calling getExchangeDistribution
      // This avoids the "execution reverted" error when the contract has no data
      if (totalPurchases === BigInt(0)) {
        debugLog('No purchases recorded, skipping exchange distribution');
        return [];
      }

      const distribution = await this.callContract<[string[], bigint[]]>(this.auditTrailContract, 'getExchangeDistribution');
      const exchanges = distribution[0];
      const amounts = distribution[1];

      const totalAmount = amounts.reduce((sum: bigint, amount: bigint) => sum + amount, BigInt(0));

      return exchanges.map((exchange: string, index: number) => {
        const amount = amounts[index];
        const percentage = totalAmount > 0
          ? (Number(amount * BigInt(10000) / totalAmount) / 100)
          : 0;

        return {
          exchange,
          amount: ethers.formatEther(amount),
          percentage
        };
      });
    } catch (error) {
      debugLog('Failed to fetch exchange distribution', error);
      // Return empty array instead of throwing to prevent UI errors
      return [];
    }
  }

  /**
   * Get BWS token information
   */
  async getTokenInfo(): Promise<TokenInfo> {
    if (!this.bwsTokenContract) {
      console.error('BWS token contract not initialized. Address:', config.blockchain.contracts.bwsToken);
      throw new Error('BWS token contract not initialized');
    }

    if (!this.vaultContract) {
      console.error('Vault contract not initialized. Address:', config.blockchain.contracts.vault);
      throw new Error('Vault contract not initialized');
    }

    try {
      // Check cache for static data (name, symbol, decimals)
      const staticCacheKey = `token-static-${config.blockchain.contracts.bwsToken}`;
      let cachedStatic = this.getCachedData<{name: string, symbol: string, decimals: number}>(staticCacheKey);

      let name: string, symbol: string, decimals: number;

      if (cachedStatic) {
        // Use cached static data
        ({ name, symbol, decimals } = cachedStatic);
        console.log('Using cached token static data:', { name, symbol, decimals });
      } else {
        // Fetch static data and cache it
        console.log('Fetching token static data from contracts...');
        [name, symbol, decimals] = await Promise.all([
          this.callContract<string>(this.bwsTokenContract, 'name'),
          this.callContract<string>(this.bwsTokenContract, 'symbol'),
          this.callContract<bigint>(this.bwsTokenContract, 'decimals').then(d => Number(d))
        ]);

        // Cache static data for 24 hours
        this.setCachedData(staticCacheKey, { name, symbol, decimals }, this.STATIC_DATA_TTL);
      }

      // Always fetch dynamic data (totalSupply, vaultBalance)
      console.log('Fetching token dynamic data...');
      const [totalSupply, vaultBalance] = await Promise.all([
        this.callContract<bigint>(this.bwsTokenContract, 'totalSupply'),
        this.callContract<bigint>(this.bwsTokenContract, 'balanceOf', config.blockchain.contracts.vault)
      ]);

      const result = {
        name,
        symbol,
        decimals,
        totalSupply: ethers.formatUnits(totalSupply, decimals),
        vaultBalance: ethers.formatUnits(vaultBalance, decimals)
      };

      console.log('Token info fetched:', {
        name,
        symbol,
        decimals: Number(decimals),
        totalSupplyRaw: totalSupply.toString(),
        totalSupplyFormatted: result.totalSupply,
        vaultBalanceRaw: vaultBalance.toString(),
        vaultBalanceFormatted: result.vaultBalance
      });

      return result;
    } catch (error) {
      console.error('Error getting token info:', error);
      debugLog('Error getting token info', error);
      throw error;
    }
  }

  /**
   * Set up event listeners for real-time updates
   */
  setupEventListeners(callbacks: {
    onRevenueDeposited?: (depositor: string, amount: string) => void;
    onPurchaseAuthorized?: (marketMaker: string, amount: string) => void;
    onTokenPurchase?: (vault: string, ethAmount: string, tokenAmount: string, exchange: string) => void;
    onTokensWithdrawn?: (trader: string, amount: string) => void;
  }): void {
    if (!this.connected) {
      console.warn('Cannot set up event listeners: blockchain service not connected');
      return;
    }

    // Treasury events
    if (this.treasuryContract && callbacks.onRevenueDeposited) {
      this.treasuryContract.on('RevenueDeposited', (depositor: string, amount: bigint) => {
        callbacks.onRevenueDeposited!(depositor, ethers.formatEther(amount));
      });
    }

    if (this.treasuryContract && callbacks.onPurchaseAuthorized) {
      this.treasuryContract.on('PurchaseAuthorized', (marketMaker: string, amount: bigint) => {
        callbacks.onPurchaseAuthorized!(marketMaker, ethers.formatEther(amount));
      });
    }

    // Audit trail events
    if (this.auditTrailContract && callbacks.onTokenPurchase) {
      this.auditTrailContract.on('TokenPurchaseRecorded', 
        (vault: string, ethAmount: bigint, tokenAmount: bigint, exchange: string) => {
          callbacks.onTokenPurchase!(
            vault,
            ethers.formatEther(ethAmount),
            ethers.formatUnits(tokenAmount, 9), // BWS has 9 decimals
            exchange
          );
        }
      );
    }

    // Vault events
    if (this.vaultContract && callbacks.onTokensWithdrawn) {
      this.vaultContract.on('TokensWithdrawnForTrading', (trader: string, amount: bigint) => {
        callbacks.onTokensWithdrawn!(trader, ethers.formatUnits(amount, 9)); // BWS has 9 decimals
      });
    }

    debugLog('Event listeners set up successfully');
  }

  /**
   * Remove all event listeners
   */
  removeEventListeners(): void {
    if (this.treasuryContract) {
      this.treasuryContract.removeAllListeners();
    }
    if (this.vaultContract) {
      this.vaultContract.removeAllListeners();
    }
    if (this.auditTrailContract) {
      this.auditTrailContract.removeAllListeners();
    }
    if (this.bwsTokenContract) {
      this.bwsTokenContract.removeAllListeners();
    }
    
    debugLog('Event listeners removed');
  }

  /**
   * Refresh contract addresses and reinitialize
   */
  async refreshContracts(addresses: {
    treasury?: string;
    vault?: string;
    auditTrail?: string;
    bwsToken?: string;
  }): Promise<void> {
    // Update config
    if (addresses.treasury) config.blockchain.contracts.treasury = addresses.treasury;
    if (addresses.vault) config.blockchain.contracts.vault = addresses.vault;
    if (addresses.auditTrail) config.blockchain.contracts.auditTrail = addresses.auditTrail;
    if (addresses.bwsToken) config.blockchain.contracts.bwsToken = addresses.bwsToken;

    // Reinitialize contracts
    await this.initializeContracts();
    
    debugLog('Contracts refreshed with new addresses', addresses);
  }

  /**
   * Check if service is connected and ready
   */
  isConnected(): boolean {
    return this.connected && !!this.provider;
  }

  /**
   * Get rate limiter status for debugging
   */
  getRateLimiterStatus() {
    return this.rateLimiter.getStatus();
  }

  /**
   * Get current block number
   */
  async getCurrentBlockNumber(): Promise<number> {
    if (!this.provider) {
      throw new Error('Provider not initialized');
    }

    return await this.callProvider<number>('getBlockNumber');
  }

  /**
   * Get current network info
   */
  async getNetworkInfo(): Promise<{ name: string; chainId: number }> {
    if (!this.provider) {
      throw new Error('Provider not initialized');
    }

    const network = await this.callProvider<{ name: string; chainId: bigint }>('getNetwork');
    return {
      name: network.name || 'Unknown',
      chainId: Number(network.chainId)
    };
  }
}

// Export singleton instance
export const blockchainService = new BlockchainService();
export default blockchainService;